import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import { TopicList } from '@/admin/components/TopicList';
import type { TopicsPageQuery } from '@/__generated__/TopicsPageQuery.graphql';

/**
 * Connection key for the topics list - used by mutations to update the list
 */
export const TOPICS_CONNECTION_KEY = 'TopicsPage_topics';

/**
 * Root query for the admin TopicsPage - fetches all topics
 */
const topicsPageQuery = graphql`
  query TopicsPageQuery {
    topics(first: 100) @connection(key: "TopicsPage_topics") {
      edges {
        node {
          id
          slug
          ...TopicList_topic
        }
      }
    }
  }
`;

/**
 * Get the connection ID for the topics list.
 * Used by mutations to add/remove topics from the list.
 */
export function getTopicsConnectionId(): string {
  return ConnectionHandler.getConnectionID('client:root', TOPICS_CONNECTION_KEY);
}

function TopicsPageContent() {
  const data = useLazyLoadQuery<TopicsPageQuery>(topicsPageQuery, {}, {
    fetchPolicy: 'store-and-network',
  });

  const topics = data.topics.edges.map((edge) => ({
    id: edge.node.id,
    fragmentRef: edge.node,
  }));
  const connectionId = getTopicsConnectionId();

  return (
    <>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Topics</h2>
          <p className="mt-1 text-gray-400">Manage your podcast topics and content sources</p>
        </div>
        <Link to="/admin/topics/create" className="btn-primary w-full sm:w-auto">
          Create Topic
        </Link>
      </div>

      <TopicList topics={topics} connectionId={connectionId} />
    </>
  );
}

export function TopicsPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      }
    >
      <TopicsPageContent />
    </Suspense>
  );
}
